/**
 * Utility to detect deployment-related errors that should show maintenance page
 */

export interface DeploymentError {
  isDeploymentError: boolean;
  errorType: 'turbo-stream' | 'network' | 'server-unavailable' | 'other';
  message: string;
}

/**
 * Checks if an error is related to deployment/maintenance issues
 */
export function detectDeploymentError(error: unknown): DeploymentError {
  const errorMessage = getErrorMessage(error);
  const lowerMessage = errorMessage.toLowerCase();

  // Also check the entire error object as a string for turbo-stream patterns
  const errorString = JSON.stringify(error).toLowerCase();


  // Check for turbo-stream decode errors (primary deployment indicator)
  if (lowerMessage.includes('unable to decode turbo-stream response') ||
      lowerMessage.includes('turbo-stream') ||
      lowerMessage.includes('decode turbo-stream') ||
      errorString.includes('unable to decode turbo-stream response') ||
      errorString.includes('turbo-stream') ||
      errorString.includes('decode turbo-stream')) {
    return {
      isDeploymentError: true,
      errorType: 'turbo-stream',
      message: 'Service temporarily unavailable during deployment'
    };
  }

  // Check for network connectivity issues during deployment
  if (lowerMessage.includes('network error') ||
      lowerMessage.includes('fetch failed') ||
      lowerMessage.includes('connection refused') ||
      lowerMessage.includes('service unavailable')) {
    return {
      isDeploymentError: true,
      errorType: 'network',
      message: 'Service temporarily unavailable'
    };
  }

  // Check for server unavailable errors
  if (lowerMessage.includes('server error') ||
      lowerMessage.includes('502 bad gateway') ||
      lowerMessage.includes('503 service unavailable') ||
      lowerMessage.includes('504 gateway timeout')) {
    return {
      isDeploymentError: true,
      errorType: 'server-unavailable',
      message: 'Service temporarily unavailable'
    };
  }

  return {
    isDeploymentError: false,
    errorType: 'other',
    message: errorMessage
  };
}

/**
 * Extracts error message from various error types
 */
function getErrorMessage(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;

  // Handle React Router error responses
  if (error && typeof error === 'object') {
    // Check for data property (route error response)
    if ('data' in error) {
      const data = (error as any).data;
      if (typeof data === 'string') return data;
      if (data && typeof data === 'object' && 'message' in data) {
        return String(data.message);
      }
    }

    // Check for statusText property
    if ('statusText' in error && error.statusText) {
      return String(error.statusText);
    }

    // Check for message property
    if ('message' in error) {
      return String(error.message);
    }
  }

  return 'An unexpected error occurred';
}

/**
 * Checks if we should show maintenance page based on error
 */
export function shouldShowMaintenancePage(error: unknown): boolean {
  const detection = detectDeploymentError(error);
  return detection.isDeploymentError;
}